import { bulletGlyph } from './transitMapGeometry'
import {
  paintDotField,
  signFont,
  signText,
  stampDotGrid,
  stampSignLabel,
  SIGN_AMBER,
  SIGN_GROUND,
  type SignPlacard,
} from './vehicleSigns'

export const R160_SIZE_SCALE = 1

// Two bodies off one shell (mta-assets `SUB-60-R160`): the B car is non-cab at both
// ends, the A car carries the full-width operator cab. Same dimensions, same sockets,
// so they interleave in a consist with no geometry changes.
//
export const R160_MODELS = {
  // L0 and L1 carry `R160_LiveryDoor` — the door leaves as a livery surface of their own,
  // so a wrap runs across them instead of being sliced by bare steel every few metres.
  // L2 is NOT bumped: the far tier folds every material into two, so it re-exported
  // byte-identical and a rev there would evict a cached model to fetch the same bytes.
  A: {
    L0: '/models/subway/SUB-60-R160-A_L0.glb?rev=11',
    L1: '/models/subway/SUB-60-R160-A_L1.glb?rev=11',
    L2: '/models/subway/SUB-60-R160-A_L2.glb?rev=9',
  },
  B: {
    L0: '/models/subway/SUB-60-R160-B_L0.glb?rev=12',
    L1: '/models/subway/SUB-60-R160-B_L1.glb?rev=12',
    L2: '/models/subway/SUB-60-R160-B_L2.glb?rev=10',
  },
} as const
export type R160Lod = keyof (typeof R160_MODELS)['A']

/**
 * The material a livery repaints, and the one surface on this body that carries texture
 * coordinates. Exported by `assign_livery_panel` in `mta-assets/scripts/export_r160.py`,
 * which moves the stainless sidewall out of `R160_Stainless` and unwraps it as a side
 * elevation — u along the car, v up it, **each flank on its own stacked island** so a wrap
 * reads the same way from either side AND a handed marking can differ between them.
 *
 * Present at L0 and L1 only. L2 folds the whole car into `R160_FarShell` and its UVs are
 * stripped on export, so a livery reaches that tier as a base COLOUR rather than a picture.
 *
 * ⚠️ The panel's default material values ARE the stainless shell's, so an unliveried car
 * looks exactly as it always has. A livery that paints it must also drop `metallicFactor`:
 * the default is 0.95 because bare stainless is a metal, and painted vinyl is not — a
 * metallic red renders as a dark mirror, not as a red train.
 */
export const R160_LIVERY_MATERIAL = 'R160_Livery'
export const R160_FAR_SHELL_MATERIAL = 'R160_FarShell'

/**
 * Which material carries each livery surface on this body — the contract between
 * `export_r160.py` and `liveryScenegraph`.
 *
 * The train has TWO where the bus has five, and the absences are deliberate rather than
 * pending work. There is no `front`/`rear`: an R160's cab is glazing and a black front
 * plate, with no skin a picture could live on. There is no `windows`: perforated vinyl
 * over the side glazing is a bus trick, and a subway car whose windows are painted stops
 * reading as a train at all.
 *
 * `doors` shares the sidewall's plane, its unwrap AND its extent, so one side elevation
 * runs straight across a door leaf — which is the entire reason it is a separate surface
 * rather than part of `R160_Livery`: whether the doors are painted is the livery's choice,
 * and merging them would take it away.
 */
export const R160_LIVERY_SURFACES = {
  sidewall: R160_LIVERY_MATERIAL,
  doors: 'R160_LiveryDoor',
} as const

/**
 * Whether this tier's GLB is worth the physically-based shader.
 *
 * `false` leaves deck's `_lighting` at its flat default, which is UNLIT: the fragment is
 * the material's base colour and nothing else — no normal, no light loop, no environment
 * sample. That is the cheapest thing the map can draw that is still a mesh, and it is the
 * right answer for L2, where a whole car is 12–48 px long and its shading is a value, not
 * a surface.
 *
 * It is only safe because the two are exported together. L2 is one **non-metallic**
 * material (`R160_FarShell`, see `LOD_MATERIAL_PLAN` in `mta-assets/scripts/export_r160.py`)
 * chosen for exactly this shader; the tiers above are the real stainless, and a metal has
 * no diffuse term, so unlighting L0/L1 would render them black rather than cheap.
 * **Change one and you must change the other.**
 */
export function r160TierUsesPbr(tier: R160Lod): boolean {
  return tier !== 'L2'
}

// The Draco load options every one of these GLBs needs are shared with the bus now, so
// they live in `gltfLoadOptions.ts` — see VENDORED_GLB_LOAD_OPTIONS, and read it before
// touching a ScenegraphLayer's loadOptions.

/**
 * The zoom at which a train becomes worth a mesh at all. Sized against the thing the
 * mesh REPLACES, not against zero: the stand-in is a `TRAIN_MARKER_BADGE_PX` (24 px)
 * bullet, so a mesh only earns the swap once the train it draws is bigger than that.
 * The map is 156543·cos 40.7°/2^z m/px — 3.6 m/px at zoom 15 — so an 18 m car is ~5 px
 * and the 144 m consist ~40 px: comfortably past the badge, and the first zoom at which
 * the silhouette reads as a train rather than a smear. A zoom lower (7.2 m/px) the whole
 * consist is 20 px and each car 2.5, which is a scenegraph draw per car to render
 * something smaller and less legible than the one quad it displaced.
 *
 * Must stay >= `TRAIN_CARS_MIN_ZOOM` so the mesh never appears as a lone stand-in car:
 * by the time anything is drawn the consist is already its full `TRAIN_CARS`.
 */
export const R160_MESH_MIN_ZOOM = 15
/**
 * Above this the close mesh is worth its polygons; between the two, L1. A car is ~10 px
 * at zoom 16 and ~5 px at the floor below, so this is roughly where L0's extra geometry
 * starts landing on pixels that exist.
 */
export const R160_MESH_L0_ZOOM = 16

/**
 * Which body mesh to draw at this zoom, or `null` for none — the lowest LOD is not a
 * mesh but the route bullet plus a heading arrow (`createTrainHeadingArrow`), which is
 * exactly the two things still legible at that size: which line, and which way. Marker
 * mode is a hard swap rather than a third glTF, because the two carry the same
 * information and the badge costs one instanced quad instead of a scenegraph per car.
 */
export function r160LodForZoom(zoom: number): R160Lod | null {
  if (zoom >= R160_MESH_L0_ZOOM) return 'L0'
  return zoom >= R160_MESH_MIN_ZOOM ? 'L1' : null
}

/**
 * The same ladder, with the rider's display setting on top of it (store/settings.ts):
 * `trains` and `both` are them overriding the floor, so keep drawing a mesh below it —
 * on the FAR LOD, because the ladder has already judged the near one's polygons wasted
 * at that size and asking for a mesh is not asking for the expensive one. `optimized`
 * rides the ladder untouched. (`labels` never reaches here: it draws no mesh at any zoom
 * and TransitMap returns its own layers before this.)
 */
export function r160FleetLod(zoom: number, alwaysMesh: boolean): R160Lod | null {
  return r160LodForZoom(zoom) ?? (alwaysMesh ? 'L1' : null)
}

/**
 * Altitude (map-metres) the floating route bullet rides at in `both` mode, where the
 * mesh it normally REPLACES is drawn underneath it. Clear of the roof crown (145.8" =
 * 3.70 m, the figure `R160_CAB_FACE_DECALS` is calibrated against) by half a metre, so
 * the bullet reads as a tag above the train rather than a sticker on its roof. The
 * marker draws with `depthTest: false`, so this only decides where on screen it hangs —
 * but on a pitched map that is the whole difference. The train analogue of
 * `BUS_LABEL_ALT`.
 */
export const R160_LABEL_ALT = 4.2 * R160_SIZE_SCALE

/** R160s couple as married sets with a cab at each end; 4-car sets make the 8 we draw. */
export const R160_SET_CARS = 4

/**
 * Which body a car in the consist is, and which way its cab points.
 * A set is A-B-B-A, so an 8-car train reads A-B-B-A | A-B-B-A: cabs at both ends of
 * the train and the two mid-train cabs meeting nose to nose, exactly how two sets
 * couple. The single car drawn when zoomed out stands in for the whole train, so it
 * is the lead cab.
 */
export type R160CarKind = 'cab-forward' | 'cab-rear' | 'body'
export function r160CarKind(carIndex: number, carCount: number): R160CarKind {
  if (carCount <= 1) return 'cab-forward'
  if (carIndex === carCount - 1) return 'cab-rear'
  const positionInSet = carIndex % R160_SET_CARS
  if (positionInSet === 0) return 'cab-forward'
  return positionInSet === R160_SET_CARS - 1 ? 'cab-rear' : 'body'
}

/**
 * Which longitudinal end of a car the cab sits at, as a sign on the car's forward
 * axis: +1 leading, −1 trailing, 0 for a B car, which has no cab at either end.
 * Decal placement keys off this — the A car cannot carry the B car's end-of-car
 * cluster at its cab end, where a car number or roundel would land on the windshield.
 */
export function r160CabEnd(kind: R160CarKind): -1 | 0 | 1 {
  if (kind === 'cab-forward') return 1
  return kind === 'cab-rear' ? -1 : 0
}

/**
 * Whether this car is the one showing red markers: the rear cab of the LAST car,
 * and nothing else. Every A car carries the same dark outboard lenses, but only the
 * trailing end of the train lights them — the lead cab is running its white
 * headlights and a mid-train cab is nose to nose with the set behind it.
 * Multi-car only, so the single car drawn zoomed out (a whole train's stand-in,
 * lead cab forward, no cab at its trailing end) correctly shows none.
 */
export function r160IsTailCar(carIndex: number, carCount: number): boolean {
  return carCount > 1 && carIndex === carCount - 1
}

/**
 * The red tail lamps on that car's cab face: the OUTBOARD lens of each pair, which
 * is the one modelled dark (`R160_LampDark`; the lit inboard lamp at |y| = 28" is
 * the headlight). Authored positions from `cab_lamps` in the generator — centres at
 * |y| = 42", z = 65", lens radius 3.25" — so the disc lands in the bore rather than
 * on the bezel around it.
 *
 * `SIZE` is the quad, not the lens: the lit core is the 6.5" lens, and the rest is
 * the bloom a lit lens throws onto its housing. It stops inside the 10.5" bezel, so
 * the glow never spills onto bare fascia. Without it the lamp is a 6.5" disc on an
 * 18 m car — correct, and invisible at every zoom short of the watch camera.
 *
 * `X` follows the same rule as `R160_CAB_DECAL_X`: a flat quad has to stand at the
 * shallowest station it spans or the bowed nose cuts it to a diagonal. Here that is
 * the quad's INBOARD edge (y = 37.12") on the lens face, which sits 1.0" proud of
 * the nose: 353.48 − 14.07·((37.12 − 19.69)/(58.6417 − 19.69))² + 1.0 = 351.66",
 * plus the same sub-inch hair. The outboard edge then floats ~4.8", which is the
 * price of a flat quad on a bow and is under a pixel at any zoom this is drawn at.
 */
export const R160_TAIL_LAMP_X = 8.953
export const R160_TAIL_LAMP_Y = 1.0668
export const R160_TAIL_LAMP_Z = 1.651
export const R160_TAIL_LAMP_SIZE = 0.248

/**
 * Whether this car has a cab whose white headlights have to be blacked out. Only the
 * head of the train runs them: the tail cab is showing red instead, and a mid-train
 * cab — the conductor's, on the A car at the joint between two married sets — is
 * coupled nose to nose with the set beside it and runs dark. Every A car is the same
 * body and its inboard lens is emissive in the glTF (`R160_LampWarm`), so "off" is
 * something that has to be drawn over the model rather than left out of it. False
 * for a B car, which has no cab face and so nothing to cover.
 */
export function r160MasksHeadlights(carIndex: number, kind: R160CarKind): boolean {
  return kind !== 'body' && carIndex !== 0
}

/**
 * The blackout disc for one of those lamps: the INBOARD fixture of each pair, centred
 * |y| = 28", z = 61" per `cab_lamps`.
 *
 * `SIZE` covers the WHOLE fixture, not the 6.5" lens face. Measured off the render,
 * what actually glows is ~11" across — the emissive material reaches the lens rim and
 * the bore behind it, and at this scale it reads as one lit disc the size of the 10.5"
 * bezel. A 13" quad is opaque to 0.9 of its radius, so 11.7" of solid black swallows
 * all of it. Discs sized to the lens (6.5") and to the bore (7.4"/9") both left a lit
 * crescent. Over-covering is free here and under-covering is not: the fascia this sits
 * on is gloss black, so the margin is invisible.
 *
 * `X` is the same shallowest-station rule as the tail lamps, at the quad's inboard
 * edge (y = 21.5"): 353.48 − 14.07·((21.5 − 19.69)/(58.6417 − 19.69))² = 353.45",
 * plus the ~1.7" the storm-door portal bead stands proud there. That leaves the bead
 * itself IN FRONT of this plane, which is what should happen — the portal's bright
 * edge is real relief and is not part of the lamp.
 */
export const R160_LAMP_MASK_X = 9.021
export const R160_LAMP_MASK_Y = 0.7112
export const R160_LAMP_MASK_Z = 1.5494
export const R160_LAMP_MASK_SIZE = 0.3302

/**
 * Whether this car shows the route bullet on its front sign: the outward-facing cab
 * of the FIRST and LAST car, and nothing else. The two mid-train cabs are coupled
 * nose to nose with the set beside them, so their signs are never seen and are dark
 * on the real train too. The single car drawn zoomed out stands in for the whole
 * train and leads with its cab, so it shows one.
 */
export function r160ShowsRouteBullet(
  carIndex: number,
  carCount: number,
  kind: R160CarKind,
): boolean {
  if (kind === 'body') return false
  return carIndex === 0 || carIndex === carCount - 1
}

/**
 * The front route sign — `SOCKET_headsign_front`, the rounded display above the storm
 * door. Authored as `cab_headsign` in the generator: centred on the car's centreline,
 * 15" wide by 9.3" tall (z 132.4"–141.7"), its face 0.25" proud of the nose inside a
 * 2.5" bezel that stands 1.0" proud. `SIZE` is the aperture HEIGHT, so the bullet is
 * inscribed in the display and never crosses that raised frame. No bow correction: the
 * sign spans |y| ≤ 4.65", well inside the 19.69" flat centre bay, so the nose is a
 * plane across all of it.
 *
 * `X` is 0.9" proud of the nose — 0.65" clear of the panel face it sits on, and just
 * behind the bezel, which should still occlude the display at a grazing angle. The
 * first pass put it 0.1" off the panel and the sign never appeared: a tenth of an inch
 * on an 18 m car is a fraction of a depth-buffer step at any zoom this is drawn at, so
 * the panel won. Every other cab-face placard clears its surface by more than this and
 * that is why they rendered first time.
 *
 * ⚠️ The user's head-on photo puts the LIT bullet at roughly 11" across — about 1.7"
 * taller than the aperture the cab drawing gave. The bullet is drawn to the modelled
 * aperture rather than the photo, because a bullet sized off the photo renders as a
 * red arc crossing the bezel. If the housing is ever re-measured, this follows it.
 */
export const R160_BULLET_X = 9.001
export const R160_BULLET_Y = 0
export const R160_BULLET_Z = 3.481
export const R160_BULLET_SIZE = 0.236

/**
 * The A car is modelled with its cab at −X (SOCKET_headsign_front sits at x = −8.98)
 * while the scenegraph layer maps mesh +X to the direction of travel. A cab that has
 * to face forward is therefore the body turned around — a yaw of half a turn.
 */
export const R160_CAB_FORWARD_YAW = 180

/**
 * The two decals on the A car's black cab face: flag above roundel, both on the
 * panel opposite the operator's windshield.
 *
 * `y` is the distance from the car centreline toward that panel and `x` the distance
 * from the car centre to the placard plane; multiply BOTH by the cab's end sign
 * (+1 when the cab leads, −1 when it trails) — the same 180° flip
 * `R160_CAB_FORWARD_YAW` applies to the body turns the model's panel side around
 * with it, so one sign moves the pair to the correct end AND the correct side.
 *
 * Traced off the user's head-on photo, calibrated on two features the model also
 * carries: the lit inboard lamps (±28", z 61") set 4.893 px/inch across and fix the
 * centreline, and lamp-to-roof-crown (z 145.8") sets 4.552 px/inch up. Flag centre
 * lands at y −34.0", z 117.1"; roundel at y −33.6", z 98.2" — i.e. both sit at very
 * nearly the mirror of the windshield's own +35.5" centre, which is the check that
 * they were read off the right side.
 *
 * ⚠️ `x` is the depth of the DEEPEST point the pair spans (the inboard edge, where
 * the nose bow is flattest) plus a hair. The nose falls back 5.3" across the flag's
 * width and an IconLayer quad is flat, so the alternative is an outboard edge buried
 * in the bodywork and clipped to a diagonal by the depth test. Standing the whole
 * decal proud instead costs ~6" of float at the outer edge — under a pixel at any
 * zoom the consist is drawn at.
 */
export const R160_CAB_DECAL_X = 8.995
export const R160_CAB_DECAL_Y = 0.859
export const R160_CAB_FACE_DECALS: ReadonlyArray<{
  kind: R160PlacardKind
  z: number
  height: number
}> = [
  // Heights are the icon's height in metres (IconLayer sizeBasis is 'height'); the
  // 1.9:1 flag is therefore ~0.51 m wide, matching its traced 20.2".
  { kind: 'flag', z: 2.974, height: 0.27 },
  { kind: 'mta', z: 2.494, height: 0.447 },
]

/**
 * ── The side decals ──────────────────────────────────────────────────────────
 *
 * Offsets on the car's own axes, in metres: `X` runs along the car from its centre
 * toward the end the decal is on, `Y` out to the sidewall, `Z` up from the rail.
 * TransitMap mirrors each through `side`, so one set of numbers covers both sides.
 *
 * **The two bodies are decalled differently, and that is the point** — see
 * `r160SideDecals`, which is the whole rule. Both carry the same END-BAY cluster,
 * number on the raised plate with the roundel under it; what differs is the front:
 *
 *   R160A — a cab end, so the end bay is at its OTHER end only, and the front takes
 *           the number on the A PILLAR (see `r160APillarX`), plus a ROUNDEL on the
 *           cab pillar immediately behind the operator's window. The US FLAG is
 *           handed: under the number on the LEFT side, but on the rear pillar between
 *           the rear door and rear window on the RIGHT side.
 *   R160B — no front to prefer, so the end-bay cluster is repeated at both ends, and
 *           ONE FLAG per side, on the A pillar to the LEFT of that side's LED sign.
 *           The sign bay is diagonal, so the flag is too — see `r160SideDecals`.
 *
 * The A-car flag is banded by what shares its pillar, not by which side it is on: the
 * left-side copy hangs UNDER the cab-end number, so it takes the low band; the
 * right-side copy stands alone on the rear pillar, so it is top-aligned with the rest
 * of the markings. The B-car flag stands beside its number and is top-aligned too.
 *
 * The photo's scale comes from the number plate: its 21.54 x 9.37" outline is
 * measured on the datasheet (`placard_x` / `placard_z` in mta-assets' generator) and
 * the plate is 51 x 27 px in the photo, so 2.76 px/inch fixes everything else.
 *
 * `Y` stands 0.8" proud of the 58.64" sidewall: enough that the quad never z-fights
 * the body it is stuck to, small enough to still read as a decal and not a card.
 */
export const R160_SIDE_DECAL_Y = 1.51

/**
 * The end-bay cluster, on the axis of the raised number plate — real geometry at x
 * 313.34..334.88", z 107.72..117.09", standing 0.5" proud, and present at every end
 * bay of both bodies. The digits take the plate's own centre and height so they cannot
 * drift off the hardware they are screwed to, and the roundel hangs centred under it.
 */
export const R160_PLATE_X = 8.232

/**
 * The A pillar: the first stretch of bare wall in from the end of the car, between the
 * outermost door and the window beside it. On the A car this is the CAB-END pillar and
 * always carries the number; its left side also carries the flag. On the B car the same
 * value locates its single side flag at whichever end the diagonal sign selects.
 *
 * Measured off each exported mesh rather than the shared generator table, because they
 * do not agree — the A car's doors and windows are re-derived from its own side
 * elevation in `configure_r160_a`, the same reason `r160SignBayX` is per body:
 *
 *   A   door frame ends 211.2", window starts 179.4"  →  31.8" centred on 195.3"
 *   B   door frame ends 237.1", window starts 201.8"  →  35.3" centred on 219.5"
 *
 * The decals are ~21.6" wide, so ~5" of body shows either side on the A car and ~7" on
 * the B car.
 */
export function r160APillarX(kind: R160CarKind): number {
  return kind === 'body' ? 5.574 : 4.953
}

/**
 * The A car's non-cab-end pillar, where the RIGHT-side flag belongs. This end is not
 * the mirror of the cab end: the cab changes the passenger-bay spacing. From the A-car
 * elevation, the rear window ends at 207.92" and the rear door frame begins at 242.07",
 * putting the pillar centre at 225.00" (5.715 m).
 */
export const R160_A_REAR_PILLAR_X = 5.715

/**
 * The A car's cab pillar: the wall immediately behind the operator's side window,
 * which ends 303.2" out, running in to the first passenger door at 265.6".
 *
 * 37.5" of pillar, but the roundel cannot be centred on it. The door-open light and
 * key flap sit in the middle at x 274.6..282.2 (`cab_pillar_x`, and DIAGONAL, so they
 * are on one side only), leaving 21.0" of clear wall against the operator window. The
 * 19.5" roundel goes there with 0.75" of body either side — which is also exactly
 * where the reference photo puts it, hard behind the window rather than mid-panel.
 */
export const R160_CAB_PILLAR_X = 7.435

/** The number, on whichever panel its body uses: the plate's measured 9.37" height,
 *  and high enough on the A car's pillar to sit in the band the plate occupies. */
export const R160_CAR_NUMBER_Z = 2.855
export const R160_CAR_NUMBER_HEIGHT = 0.238

/**
 * Where a decal sits when its TOP edge lines up with the car number's — the B car's
 * flag, which stands beside the plate rather than under it, the A car's right-side
 * flag, which has its rear pillar to itself, and the A car's cab roundel. Derived
 * rather than written down three times, so they all follow if the band is re-measured.
 */
export function r160TopAlignedZ(height: number): number {
  return R160_CAR_NUMBER_Z + R160_CAR_NUMBER_HEIGHT / 2 - height / 2
}

/** The flag: the 1.9:1 SVG at 11.4" tall comes out 21.7", as wide as the number (the
 *  plate's 21.54"). `Z` is the LOW band, for the one copy that hangs UNDER a number on
 *  the same pillar — the A car's left side. Its right-side copy has the rear pillar to
 *  itself and the B car's stands beside its number, so both of those take
 *  `r160TopAlignedZ`. All three clear the belt moulding at z 77.5..79.0". */
export const R160_FLAG_Z = 2.515
export const R160_FLAG_HEIGHT = 0.29

/** The roundel: the 19.5" white disc carrying the roundel over the "New York City /
 *  Subway" lockup. `Z` is the end bay's, hung under the number plate with ~2" of body
 *  between; the A car's cab roundel is top-aligned with the number instead. */
export const R160_ROUNDEL_Z = 2.428
export const R160_ROUNDEL_SIZE = 0.495

export type R160SideDecal = {
  kind: Extract<R160PlacardKind, 'car-number' | 'flag' | 'mta'>
  /** Signed distance along the car from its centre, in metres — travel frame, so the
   *  A car's half-turn is already accounted for by `r160CabEnd`. */
  x: number
  z: number
  height: number
}

/**
 * Every sidewall decal one car carries on ONE side, which body it is decides entirely.
 *
 * The end-bay cluster is common: the number on the plate with the roundel under it, at
 * each end that HAS an end bay. An A car's cab end does not — the operator's window is
 * there — so that end takes the front cluster instead, and the B car, having two end
 * bays and no front, simply repeats.
 *
 * `side` is why this takes an argument at all. On an A car it is expressed in the travel
 * frame, so physical RIGHT is `side === -cabEnd`: that side's flag is on the non-cab-end
 * rear pillar, while physical LEFT keeps it under the cab-end number. This relation must
 * use `cabEnd`, rather than hard-coding side −1, because a cab-rear A car is rotated 180°.
 *
 * The B car's single flag goes to the LEFT of that side's LED sign, and the sign bay is
 * DIAGONAL — at −x on one side of the car and +x on the other (`sign_bay` and the C2
 * note in the generator). Left of it is therefore the A pillar on whichever end that
 * side's bay is at, which is the same `side * …` the sign itself is emitted with.
 */
export function r160SideDecals(kind: R160CarKind, side: number): readonly R160SideDecal[] {
  const cabEnd = r160CabEnd(kind)
  const pillar = r160APillarX(kind)
  const endBay = (end: number): R160SideDecal[] => [
    { kind: 'car-number', x: end * R160_PLATE_X, z: R160_CAR_NUMBER_Z, height: R160_CAR_NUMBER_HEIGHT },
    { kind: 'mta', x: end * R160_PLATE_X, z: R160_ROUNDEL_Z, height: R160_ROUNDEL_SIZE },
  ]
  if (cabEnd) {
    // `side` is in the travel frame. Facing the cab, the car's physical right side is
    // therefore −cabEnd; its flag belongs at the non-cab-end (rear) pillar. Nothing
    // sits above it there, so it takes the top-aligned band the rest of the markings
    // share instead of the low band the left-side copy needs to clear its number.
    const rearPillarFlag = side === -cabEnd
    const flag: R160SideDecal = {
      kind: 'flag',
      x: rearPillarFlag ? -cabEnd * R160_A_REAR_PILLAR_X : cabEnd * pillar,
      z: rearPillarFlag ? r160TopAlignedZ(R160_FLAG_HEIGHT) : R160_FLAG_Z,
      height: R160_FLAG_HEIGHT,
    }
    return [
      ...endBay(-cabEnd),
      { kind: 'car-number', x: cabEnd * pillar, z: R160_CAR_NUMBER_Z, height: R160_CAR_NUMBER_HEIGHT },
      flag,
      {
        kind: 'mta', x: cabEnd * R160_CAB_PILLAR_X,
        z: r160TopAlignedZ(R160_ROUNDEL_SIZE), height: R160_ROUNDEL_SIZE,
      },
    ]
  }
  return [
    ...endBay(-1),
    ...endBay(1),
    {
      kind: 'flag', x: side * pillar,
      z: r160TopAlignedZ(R160_FLAG_HEIGHT), height: R160_FLAG_HEIGHT,
    },
  ]
}

/**
 * The side sign fills its bay's ENTIRE upper sash. The datasheet drew two recessed
 * insets up there, a 5.63" bullet square and a 32.76" strip, and this used to be two
 * placards matching them exactly. It is one display: the reference photo shows the
 * whole sash dark with a single matrix across it, and those insets are the housing
 * behind the glass.
 *
 * `Y` sits in the window's RECESS (58.64" − the 1.75" the sash is set back), not on
 * the sidewall the other decals ride. A sign standing proud of the wall would cover
 * the window frame's inner edge; down in the recess the frame occludes it at a
 * grazing angle, which is what a recessed display does. Half an inch of clearance off
 * the panel, not a hair: the front bullet went invisible at 0.1", where the depth test
 * cannot separate the quad from the face behind it. Every bay is recessed the same
 * 1.75", so this one figure serves all of them.
 */
export const R160_SIDE_SIGN_Y = 1.458

/** One bay's upper sash, which is exactly the sign — see `r160SignBay`. */
export type R160SignBay = {
  /** Signed distance along the car from its centre to the bay's centre, in the travel
   *  frame — so the A car's half-turn is already accounted for, as in `R160SideDecal`. */
  x: number
  /** Centre of that sash above the rail. */
  z: number
  /** The sash's height. IconLayer sizes on height, so this is what is asked for. */
  height: number
  /** The sash's width ÷ its height, which is therefore how its width is asked for. The
   *  canvas has to carry this or the sign comes out the wrong width. */
  aspect: number
}

// Every bay on both bodies is the same 48.9" wide, so only the sash HEIGHT varies, and
// with it the aspect the width is asked for through.
//
// The signage bay's sash is the one the mesh models a sign PANEL in: its transom bar
// sits 1.51" lower than a plain bay's (`win_transom_signage`), so it runs 106.66" up to
// the 118.96" window head — 12.31" tall, centred on 112.81". A PLAIN bay's transom is at
// 106.91", leaving 108.17" to the same head: 10.80" tall, centred on 113.56". The A car
// needs both; see `r160SignBay`.
const R160_SIGN_SASH = { z: 2.865, height: 0.3125, aspect: 48.9 / 12.305 }
const R160_PLAIN_SASH = { z: 2.884, height: 0.2742, aspect: 48.9 / 10.795 }

/**
 * Which bay carries the sign on this side of this body, and the sash it has to fill.
 *
 * The bay is DIAGONAL — at one end of the car on one side and at the other end on the
 * other (`sign_bay` and the C2 note in the generator). On the B car that is a pure sign
 * flip, because its bays sit symmetrically at ±177.34" and the mesh models a sign panel
 * in each. **On the A car it is not**, and that is what this function exists for: the
 * cab eats the front of the car, so its windows are re-derived from its own side
 * elevation and the outermost bay of each end lands at a DIFFERENT distance from centre
 * — 154.95" at the cab end, 183.48" at the other. Mirroring one constant put the far
 * side's sign at 154.95" from the wrong end, 28.5" short of its bay: it reached only the
 * inner 20" of the 48.9" sash and hung the rest onto the bare wall in front of it.
 *
 * Keying off `r160CabEnd` is what makes the modelled panel come out on the correct side.
 * `R160_CAB_FORWARD_YAW` turns the whole body half a turn, so mesh x = −travel x on a
 * cab-forward car and +travel x on a cab-rear one; `side === cabEnd` is the side that
 * lands on mesh x −154.95", which is where the A car's one sign panel is.
 *
 * ⚠️ The exported A car has that panel on ONE side only. The generator picks the
 * signage bay with `sign_cx = sign_bay * t`, i.e. by mirroring the bay's x — and +154.95"
 * matches no A-car window, so the other side builds three plain bays and no panel. The
 * sign rides a plain glazed sash there, which is why this returns two sash sizes; fix
 * `side_apertures` upstream to select the far END rather than the mirrored x and both
 * sides collapse back to `R160_SIGN_SASH`.
 */
export function r160SignBay(kind: R160CarKind, side: number): R160SignBay {
  const cabEnd = r160CabEnd(kind)
  // B car: 177.34" at both ends, panel modelled in both. A car: 154.95" at the cab end,
  // where the panel is, and 183.48" at the other, where it is a plain sash.
  if (!cabEnd) return { x: side * 4.504, ...R160_SIGN_SASH }
  return side === cabEnd
    ? { x: side * 3.936, ...R160_SIGN_SASH }
    : { x: side * 4.66, ...R160_PLAIN_SASH }
}

/**
 * One placard on one car, positioned on the car's own axes: `x` runs along the car from
 * its centre toward the end the placard is on, `y` out to the surface it rides, `z` up
 * from the rail. Metres throughout.
 */
export type R160Placement = {
  kind: 'side-sign' | 'front-bullet' | R160PlacardKind
  x: number
  y: number
  z: number
  /** Which side (sidewall) or end (cab face) the shader reads for reading direction. */
  side: number
  /** The icon's HEIGHT in metres — IconLayer's sizeBasis defaults to 'height', so the
   *  1.9:1 flag comes out as wide as the number above it. */
  height: number
  /** 0 lays the placard ALONG the car (a sidewall decal), 1 across it (a cab face). */
  facing: 0 | 1
  /** Side signs only: the sash's width ÷ height, which is what the matrix is drawn to.
   *  The A car's two bays differ — see `r160SignBay`. */
  aspect?: number
}

const r160PlacementCache = new Map<string, readonly R160Placement[]>()

/**
 * Every placard one car of a consist carries, both sides and both ends.
 *
 * This is where the ten rules above meet: which body this car is (`r160CarKind`), which
 * end its cab is on (`r160CabEnd`), which sidewall cluster that body takes
 * (`r160SideDecals`), which bay holds its sign (`r160SignBay`), and whether it is the
 * train's tail (`r160IsTailCar`), its head (`r160ShowsRouteBullet`) or a cab that has to
 * black its headlights out (`r160MasksHeadlights`). Assembling it beside those rules
 * rather than in the map component is what makes the assembly testable at all, and stops
 * the constants from being read 200 lines and one language away from where they are
 * documented.
 *
 * Memoised because the answer depends on nothing but `(carIndex, carCount)` — there are
 * only a handful of distinct answers, and the map's rAF loop asks for one per car per
 * train per frame. Frozen so a caller cannot mutate the shared reply.
 */
export function r160CarPlacards(
  carIndex: number,
  carCount: number,
): readonly R160Placement[] {
  const key = `${carIndex}|${carCount}`
  const cached = r160PlacementCache.get(key)
  if (cached) return cached

  const carKind = r160CarKind(carIndex, carCount)
  const cabEnd = r160CabEnd(carKind)
  const placements: R160Placement[] = []

  for (const side of [-1, 1]) {
    // Which bay this side's sign fills, and the sash it has to fill — per SIDE as well
    // as per body on the A car, whose two ends put their outermost window at different
    // distances from the centre.
    const bay = r160SignBay(carKind, side)
    // One display filling that upper sash, bullet and destination both inside it. It
    // rides the recessed sash face, not the sidewall the decals below sit on.
    placements.push({
      kind: 'side-sign', x: bay.x, y: side * R160_SIDE_SIGN_Y, z: bay.z,
      side, height: bay.height, aspect: bay.aspect, facing: 0,
    })
    // The sidewall cluster this body carries, wherever it carries it.
    for (const decal of r160SideDecals(carKind, side)) {
      placements.push({
        kind: decal.kind, x: decal.x, y: side * R160_SIDE_DECAL_Y, z: decal.z,
        side, height: decal.height, facing: 0,
      })
    }
  }

  if (cabEnd) {
    // Cab face: flag over roundel, opposite the windshield. `side` is the cab end here
    // because the placard reads left-to-right for whoever is stood in front of THAT
    // end — reversed at each end of the train.
    for (const decal of R160_CAB_FACE_DECALS) {
      placements.push({
        kind: decal.kind, x: cabEnd * R160_CAB_DECAL_X, y: cabEnd * R160_CAB_DECAL_Y,
        z: decal.z, side: cabEnd, height: decal.height, facing: 1,
      })
    }
    // Red markers, last car only — the train's tail lights. Same cab face and the same
    // end sign as the decals above; the pair is symmetric about the centreline, so both
    // lateral offsets are emitted rather than mirrored through `side`.
    if (r160IsTailCar(carIndex, carCount)) {
      for (const lampSide of [-1, 1]) {
        placements.push({
          kind: 'tail-lamp', x: cabEnd * R160_TAIL_LAMP_X, y: lampSide * R160_TAIL_LAMP_Y,
          z: R160_TAIL_LAMP_Z, side: cabEnd, height: R160_TAIL_LAMP_SIZE, facing: 1,
        })
      }
    }
    // The front sign, above the storm door: the route bullet, on the outward-facing cab
    // of the first and last car. Centred on the car, so `cabEnd` only has to pick the
    // end and the reading direction.
    if (r160ShowsRouteBullet(carIndex, carCount, carKind)) {
      placements.push({
        kind: 'front-bullet', x: cabEnd * R160_BULLET_X, y: cabEnd * R160_BULLET_Y,
        z: R160_BULLET_Z, side: cabEnd, height: R160_BULLET_SIZE, facing: 1,
      })
    }
    // ...and the inboard pair blacked out on every cab except the one leading the train,
    // since the model bakes those lenses lit.
    if (r160MasksHeadlights(carIndex, carKind)) {
      for (const lampSide of [-1, 1]) {
        placements.push({
          kind: 'lamp-off', x: cabEnd * R160_LAMP_MASK_X, y: lampSide * R160_LAMP_MASK_Y,
          z: R160_LAMP_MASK_Z, side: cabEnd, height: R160_LAMP_MASK_SIZE, facing: 1,
        })
      }
    }
  }

  const frozen = Object.freeze(placements)
  r160PlacementCache.set(key, frozen)
  return frozen
}

// The layer these placements are drawn through — `VehicleSideIconLayer` — and the LED
// matrix the signs below are stamped on are shared with the bus now, so they live in
// `vehicleSigns.ts`. Read the `side`/`facing` contract there before adding a placard.

export type R160PlacardKind =
  'car-number' | 'flag' | 'mta' | 'tail-lamp' | 'lamp-off'

// deck.gl packs auto-icons into a 1024 px-wide atlas and reserves exactly the
// width/height declared here — an icon wider than the atlas is silently clipped
// (the flag used to declare its 7410x3900 viewBox and rendered as a stretched
// slice of the canton). These MUST match each SVG's intrinsic width/height, and
// both must stay well under 1024.
const R160_STATIC_PLACARDS: Record<'flag' | 'mta', SignPlacard> = {
  flag: { url: '/images/us-flag.svg?rev=2', width: 304, height: 160 },
  mta: { url: '/images/mta-logo.svg?rev=2', width: 320, height: 320 },
}

// 20 LEDs over the sash's 12.3" is a 0.62" pitch, in the range real transit matrices
// run. It is also the resolution the CONTENT needs: the display is 4:1 and the bullet
// takes the first 1:1 of it, so a destination only gets ~3 display-heights of room and
// a long one has to render at half height — at 12 rows that half height was 6 LEDs of
// stroke and every glyph came out a blob.
const R160_SIDE_SIGN_ROWS = 20
const R160_SIDE_SIGN_PITCH = 4
// How far the type may be condensed before it starts getting SHORTER instead. Real
// signs do both, in that order; squeezing the whole way (which is all this used to do)
// is what turns "34 ST HUDSON YARDS" into a picket fence.
const R160_SIDE_SIGN_CONDENSE = 0.82

const r160SideSignCache = new Map<string, SignPlacard>()
/**
 * The side sign: ONE amber matrix filling the bay's whole upper sash, with the route
 * bullet at the reading-left end and the destination beside it. This replaced a pair
 * of placards drawn to the datasheet's two recessed insets — see `R160_SIDE_SIGN_Y` for
 * why the sash is the display and the insets are not.
 *
 * The canvas aspect IS the sign's width on the car (IconLayer sizes on height), so it
 * is passed in from the bay being filled rather than picked — and the A car's two bays
 * are not the same shape, so it is part of the cache key.
 */
export function createR160SideSign(routeId: string, text: string, aspect: number): SignPlacard {
  const key = `${routeId}:${text}:${aspect}`
  const cached = r160SideSignCache.get(key)
  if (cached) return cached

  const rows = R160_SIDE_SIGN_ROWS
  const pitch = R160_SIDE_SIGN_PITCH
  const canvas = document.createElement('canvas')
  canvas.height = (rows + 1) * pitch
  canvas.width = Math.round(canvas.height * aspect)
  const ctx = canvas.getContext('2d')!
  ctx.fillStyle = SIGN_GROUND
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  const cols = Math.floor(canvas.width / pitch) - 1
  const ox = (canvas.width - cols * pitch) / 2 + pitch / 2
  const oy = (canvas.height - rows * pitch) / 2 + pitch / 2
  paintDotField(ctx, ox, oy, cols, rows, pitch)

  // Bullet and destination go into ONE mask, so they land on one grid. Two stamps at
  // two origins would let the bullet's dots fall between the text's, and the seam
  // between the two pitches is exactly what makes a single display read as two signs
  // stuck together — which is what this change was undoing.
  const sample = 4
  const mask = document.createElement('canvas')
  mask.width = cols * sample
  mask.height = rows * sample
  const mctx = mask.getContext('2d', { willReadFrequently: true })!
  mctx.textAlign = 'center'
  mctx.textBaseline = 'middle'
  mctx.fillStyle = '#fff'
  mctx.strokeStyle = '#fff'

  // The bullet is a square as tall as the display, at the left end.
  const bullet = rows * sample
  const c = bullet / 2
  mctx.lineWidth = bullet * 0.11
  mctx.beginPath()
  mctx.arc(c, c, c - mctx.lineWidth / 2 - sample * 0.5, 0, Math.PI * 2)
  mctx.stroke()
  mctx.font = signFont(Math.round(bullet * 0.52))
  mctx.fillText(bulletGlyph(routeId), c, c)

  // The destination fills what the bullet leaves: condensed down to
  // R160_SIDE_SIGN_CONDENSE, then shrunk in both axes past that, so a long name loses
  // height rather than collapsing into vertical bars. See `stampSignLabel`.
  const textX = bullet + sample * 1.5
  stampSignLabel(
    mctx, signText(text) || 'IN SERVICE',
    textX, mask.width - textX - sample * 0.5, mask.height / 2,
    rows * sample, R160_SIDE_SIGN_CONDENSE,
  )

  stampDotGrid(ctx, mask, cols, rows, sample, ox, oy, pitch, SIGN_AMBER)
  const result = { url: canvas.toDataURL(), width: canvas.width, height: canvas.height }
  r160SideSignCache.set(key, result)
  return result
}

// The front sign is a RED matrix, not the amber of the side signs and not the route's
// own color: an A train shows a red bullet on the nose and a blue one everywhere else,
// which is what both reference photos show. Route color would be the wrong detail here.
const R160_BULLET_RED = '#ff2a17'

const r160FrontBulletCache = new Map<string, SignPlacard>()
/**
 * The route bullet on the cab's front sign: a ring of LEDs with the route glyph
 * inside, on the black display ground. Square, because the aperture is filled by the
 * bullet's circle and `R160_BULLET_SIZE` is the display's height — the unlit margin
 * either side of the circle is display, not padding.
 */
export function createR160FrontBullet(routeId: string): SignPlacard {
  const cached = r160FrontBulletCache.get(routeId)
  if (cached) return cached
  // 15 LEDs across the ~9" display is roughly the real pitch, and an odd count puts a
  // dot on the centreline so the glyph sits square in the ring.
  const rows = 15
  const pitch = 4
  const size = rows * pitch
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')!
  ctx.fillStyle = SIGN_GROUND
  ctx.fillRect(0, 0, size, size)

  const sample = 4
  const mask = document.createElement('canvas')
  mask.width = rows * sample
  mask.height = rows * sample
  const mctx = mask.getContext('2d', { willReadFrequently: true })!
  const c = mask.width / 2
  mctx.strokeStyle = '#fff'
  mctx.lineWidth = mask.width * 0.1
  mctx.beginPath()
  mctx.arc(c, c, c - mctx.lineWidth / 2 - sample * 0.5, 0, Math.PI * 2)
  mctx.stroke()

  mctx.fillStyle = '#fff'
  mctx.font = signFont(Math.round(mask.height * 0.5))
  mctx.textAlign = 'center'
  mctx.textBaseline = 'middle'
  mctx.fillText(bulletGlyph(routeId), c, c)

  // Fatter dots than the side signs run: the whole sign is ~25 px on screen even at
  // the watch camera, so the atlas icon is downsampled hard and the black between
  // LEDs averages into the red. At the side signs' 0.3 the bullet reads maroon.
  stampDotGrid(ctx, mask, rows, rows, sample, pitch / 2, pitch / 2, pitch, R160_BULLET_RED, 0.46)
  const result = { url: canvas.toDataURL(), width: size, height: size }
  r160FrontBulletCache.set(routeId, result)
  return result
}

// A cab lamp: one radial gradient filling a 64 px disc. Both lamps are the same canvas
// and differ only in where the gradient's inner circle sits and what its stops are, so
// the stop lists — which are the part carrying the reasoning — are the data below and
// the drawing is written once.
const R160_LAMP_SIZE = 64
const r160LampCache = new Map<string, SignPlacard>()
function r160Lamp(
  key: string,
  innerCentreYRatio: number,
  stops: ReadonlyArray<readonly [number, string]>,
): SignPlacard {
  const cached = r160LampCache.get(key)
  if (cached) return cached
  const size = R160_LAMP_SIZE
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')!
  const c = size / 2
  const lens = ctx.createRadialGradient(c, c * innerCentreYRatio, 0, c, c, c)
  for (const [offset, color] of stops) lens.addColorStop(offset, color)
  ctx.fillStyle = lens
  ctx.beginPath()
  ctx.arc(c, c, c, 0, Math.PI * 2)
  ctx.fill()
  const result = { url: canvas.toDataURL(), width: size, height: size }
  r160LampCache.set(key, result)
  return result
}

// A lit red marker lens: a hot core out to the lens rim (0.66 of the quad — see
// R160_TAIL_LAMP_SIZE), then bloom fading to nothing by the bezel's outer edge. The
// fade is what keeps the square quad from showing its corners against the
// gloss-black fascia, so the last stop must stay fully transparent.
const R160_TAIL_LAMP_STOPS = [
  [0, '#ffe8e2'],                    // filament hotspot
  [0.22, '#ff3f30'],
  [0.52, '#d5090f'],
  [0.66, 'rgba(122, 4, 10, 0.92)'],  // lens rim
  [0.82, 'rgba(214, 26, 20, 0.28)'], // bloom across the bezel
  [1, 'rgba(255, 60, 40, 0)'],
] as const

// An unlit fixture, for covering the glTF's emissive headlight on every cab that is
// not leading the train. Near-black like the modelled dark lens beside it. Opaque out
// to 0.9 of the radius (see R160_LAMP_MASK_SIZE — that is what has to clear the lit
// area) and feathered only past that, so the fade lands on black fascia.
const R160_LAMP_OFF_STOPS = [
  [0, '#16161c'],                    // faint sky catch on the glass
  [0.5, '#0d0d10'],
  [0.86, '#191920'],                 // still inside the lens
  [0.9, 'rgba(25, 25, 32, 0.9)'],
  [1, 'rgba(25, 25, 32, 0)'],
] as const

const r160PlacardCache = new Map<string, SignPlacard>()
export function createR160Placard(kind: R160PlacardKind, text = ''): SignPlacard {
  if (kind === 'flag' || kind === 'mta') return R160_STATIC_PLACARDS[kind]
  if (kind === 'tail-lamp') return r160Lamp(kind, 1, R160_TAIL_LAMP_STOPS)
  if (kind === 'lamp-off') return r160Lamp(kind, 0.7, R160_LAMP_OFF_STOPS)
  const key = `${kind}:${text}`
  const cached = r160PlacardCache.get(key)
  if (cached) return cached
  // The car number, and only the car number: a 46x20 canvas against the plate's
  // 21.54 x 9.37" is 2.3:1 either way, so the digits fill the plate they sit on.
  const canvas = document.createElement('canvas')
  canvas.width = 46
  canvas.height = 20
  const ctx = canvas.getContext('2d')!
  ctx.fillStyle = '#171d27'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.fillStyle = '#f3f4f5'
  ctx.font = 'bold 12px sans-serif'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(text.slice(-5), canvas.width / 2, canvas.height / 2)
  const result = { url: canvas.toDataURL(), width: canvas.width, height: canvas.height }
  r160PlacardCache.set(key, result)
  return result
}
